import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { toast } from "sonner";

export default function TestimonialForm({ farmerId = null, farmerName = null, onSubmitted }) {
  const { user } = useAuth();
  const [quote, setQuote] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  if (!user) {
    return (
      <div className="bg-stone-50 border border-stone-200 rounded-md p-6 text-sm text-stone-600 font-sans">
        Sign in to share your experience.
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (quote.trim().length < 10) {
      toast.error("Please write at least a sentence.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { quote };
      if (rating > 0) payload.rating = rating;
      if (farmerId) payload.farmer_id = farmerId;
      await api.post("/testimonials", payload);
      toast.success("Thank you — your testimonial is pending review.");
      setQuote("");
      setRating(0);
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-stone-200 rounded-md p-7" data-testid="testimonial-form">
      <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Feedback</div>
      <h3 className="mt-2 font-serif text-2xl text-stone-900">
        {farmerName ? `Tell us about ${farmerName}` : "Share your experience"}
      </h3>
      <p className="mt-2 text-sm text-stone-600 font-sans">
        Approved testimonials may appear on our landing page{farmerName ? " and on this hobby farmer's profile" : ""}. We never share your email.
      </p>

      <div className="mt-5 flex items-center gap-1" data-testid="testimonial-rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`tm-star-${n}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setRating(n)}
            className={(hover || rating) >= n ? "text-amber-600" : "text-stone-300"}
            aria-label={`Rate ${n} star`}
          >
            <Star size={22} fill="currentColor" />
          </button>
        ))}
        {rating > 0 && <span className="ml-3 text-sm text-stone-500 font-sans">{rating}/5</span>}
      </div>

      <Textarea
        data-testid="tm-quote"
        rows={4}
        maxLength={600}
        value={quote}
        onChange={(e) => setQuote(e.target.value)}
        placeholder="What did you love? What surprised you? Tell us in a few sentences."
        className="mt-5 bg-stone-50"
      />
      <div className="mt-1 text-xs text-stone-500 font-sans text-right">{quote.length}/600</div>

      <Button data-testid="tm-submit" type="submit" disabled={submitting} className="mt-2 bg-emerald-800 hover:bg-emerald-700 text-stone-50">
        {submitting ? "Submitting..." : "Submit Testimonial"}
      </Button>
    </form>
  );
}
